export class Color {
  // Centralized color mapping for metrics by subsystem and metric name (camelCase)
  static colors = [
    "#ea6767",
    "#4e669d",
    "#3e8c8c",
    "#ff9b9b",
    "#ffc89b",
    "#69adad",
    "#8c7cbf",
    "#82d682",
    "#ffde9b",
    "#798dbb",
    "#efe57c",
    "#d2f193",
    "#d682b2",
    "#ffee9b",
    "#a275bb",
    "#eaa267",
    "#6451a1",
    "#ead367",
    "#52bb52",
    "#eae36b",
    "#b1da60",
    "#bb538e",
    "#eabe67",
    "#7f499c",
    "#aa2121",
    "#146666",
    "#aa5f21",
    "#352175",
    "#1a881a",
    "#aa9221",
    "#203872",
    "#aaaa21",
    "#749e1f",
    "#881a58",
    "#aa7c21",
    "#531b71",
    "#830606",
    "#200e5a",
    "#833f06",
    "#044e4e",
    "#838306",
    "#056905",
    "#835906",
    "#0d2457",
    "#537a06",
    "#836d06",
    "#69053e",
    "#3b0957"
  ];

  static aggregated(length: number): string[] {
    return this.colors.slice(0, length);
  }

  static getIndexColor(index: number) {
    return this.colors[index % this.colors.length];
  }

  static palette = {
    black: '#000000',
    blackDark: '#000000',
    blackLight: '#444444',
    blackMedium: '#222222',
    blue: '#8ad0e8',
    blueBright: '#bfe6fa', // 30% lighter than blue
    blueDim: '#5a9bb3',    // 30% darker than blue
    blueDark: '#1f77b4',
    blueLight: '#d6f3fa',
    blueMedium: '#4eb3d3',
    brown: '#db9b8c',
    brownDark: '#8a3324',
    brownLight: '#f3e0da',
    brownMedium: '#b97a5a',
    cyan: '#00ffff',
    cyanDark: '#008b8b',
    cyanLight: '#b3ffff',
    cyanMedium: '#40e0d0',
    gray: '#afafaf',
    grayBright: '#e6e6e6', // 30% lighter than gray
    grayDim: '#7a7a7a',    // 30% darker than gray
    grayDark: '#808080',
    grayLight: '#e0e0e0',
    grayMedium: '#cccccc',
    green: '#adeb9e',
    greenBright: '#c6f3b8',// 30% lighter than green
    greenDim: '#6e9e5e',   // 30% darker than green
    greenDark: '#2ca02c',
    greenLight: '#e3f8d9',
    greenMedium: '#7ed957',
    khaki: '#8a865d',
    khakiDark: '#6b6b47',
    khakiLight: '#e5e3c7',
    khakiMedium: '#bdb76b',
    orange: '#ffc89b',
    orangeBright: '#ffe2c2',// 30% lighter than orange
    orangeDim: '#b37c4d',  // 30% darker than orange
    orangeDark: '#e98b2a',
    orangeLight: '#ffe6cc',
    orangeMedium: '#ffae5e',
    purple: '#d7afe8',
    purpleDark: '#873db1',
    purpleLight: '#f0e0fa',
    purpleMedium: '#b97ad6',
    red: '#ff9895',
    redBright: '#ffd1ce',  // 30% lighter than red
    redDim: '#b36a68',     // 30% darker than red
    redDark: '#d62728',
    redLight: '#ffd6d5',
    redMedium: '#ea6767',
    totalBlue: '#1f77b4',
    totalBlueDark: '#1f77b4',
    totalBlueLight: '#8ad0e8',
    totalBlueMedium: '#4eb3d3',
    totalRed: '#d62728',
    totalRedDark: '#d62728',
    totalRedLight: '#ff9895',
    totalRedMedium: '#ea6767',
    yellow: '#fff055',
    yellowDark: '#d3be00',
    yellowLight: '#fffbcf',
    yellowMedium: '#ffe066',
  };

  static metric = {
    primary: this.palette.redDark,
    secondary: this.palette.blueDark,
    tertiary: this.palette.greenDark,
    extended: {
      first: this.palette.orangeDark,
      second: this.palette.yellowDark,
      third: this.palette.purpleDark,
      fourth: this.palette.brownDark,
      fifth: this.palette.blackDark,
    },
    areaPrimary: this.palette.red,
    areaSecondary: this.palette.blue,
    areaTertiary: this.palette.green,

    used: this.palette.red,
    free: this.palette.green,
    available: this.palette.blue,
    total: this.palette.black,
    freeArea: this.palette.green,
    usedArea: this.palette.red,
    read: this.palette.blueDark,
    write: this.palette.redDark,
    success: this.palette.greenDark,
    fail: this.palette.redDark,
    cpu: {
      free: this.palette.green,
      used: this.palette.red,
      total: this.palette.blue,
      available: this.palette.black,
    },
    memory: {
      free: this.palette.green,
      used: this.palette.red,
      reserved: this.palette.orange,
      total: this.palette.blue,
      swap: this.palette.blueDark,
      cache: this.palette.blueDark,
      available: this.palette.blackDark,
    },
    swap: {
      free: this.palette.green,
      used: this.palette.red,
      total: this.palette.blue,
      swap: this.palette.blueDark,
      available: this.palette.black,
    },
    data: {
      throughput: {
        read: this.palette.blueDark,
        write: this.palette.redDark,
      },
      capacity: {
        free: this.palette.green,
        used: this.palette.red,
        total: this.palette.blue,
        available: this.palette.black,
      },
      latency: {
        read: this.palette.blueDark,
        write: this.palette.redDark,
      }
    },
    network: {
      throughput: {
        read: this.palette.redDark,
        write: this.palette.blueDark,
      },
      error: {
        read: this.palette.redDark,
        write: this.palette.blueDark,
      }
    },
    iops: {
      read: this.palette.blueDark,
      write: this.palette.redDark,
    }
  };

  // Health Status Colors
  static health = {

    ok: '#8FD400',
    critical: '#e32636',
    warning: '#ffa500',
    unknown: '#94c5f9',
    ignored: '#d3d3d3',
    undefined: '#d3d3d3',
    test: '#d3d3d3', // Color for test status

    dim: {
      ok: '#8FC400',
      critical: '#e32636',
      warning: '#efa500',
      unknown: '#94c5e9',
      ignored: '#d3d3d3',
      undefined: '#d3d3d3',
      test: '#d3d3d3', // Color for test status
    },

    mirror: {
      ok: '#8FD400',
      error: '#e32636',
      warning: '#ffa500',
      unknown: '#94c5f9',
    }
  } as const;

  static job = {
    status: {
      notActive: '#ecf0f1',   // Light gray - not active
      running: '#27ae60',   // Green - running
      pending: '#f39c12',   // Orange - pending
      suspended: '#e74c3c',    // Red - suspended/interrupted
    }
  } as const;

  static commvault = {
    severity: {
      fatal: '#f5c6cb',
      error: '#f5c6cb',
      critical: '#f5c6cb',
      major: '#f8d7da',
      warning: '#fff3cd',
      minor: '#fff3cd',
      info: '#d1ecf1',
      information: '#d1ecf1',
      inactivewarning: '#fff3cd',
      configuration: '#d1ecf1',
      ok: '#d4edda',
      default: '#e2e3e5'
    }
  } as const;


  static tape = {
    severity: {
      fatal: '#f5c6cb',
      error: '#f5c6cb',
      critical: '#f5c6cb',
      major: '#f8d7da',
      warning: '#fff3cd',
      minor: '#fff3cd',
      info: '#d1ecf1',
      information: '#d1ecf1',
      inactivewarning: '#fff3cd',
      configuration: '#d1ecf1',
      ok: '#d4edda',
      default: '#e2e3e5'
    },


    status: {
      unloaded: '#95a5a6',
      null: '#95a5a6',
      online: '#ecf0f1',
      empty: '#ecf0f1',
      ready: '#27ae60',
      loading: '#f39c12',
      unloading: '#9b59b6',
      cleaning: '#e67e22',
      locating: '#3498db',
      rewinding: '#3498db',
      reading: '#e74c3c',
      writing: '#c0392b',
      erasing: '#8e44ad',
      formatting: '#8e44ad',
      calibrating: '#e67e22',
      updating: '#e67e22'
    },

    state: {
      ok: '#d4edda',
      good: '#d4edda',
      online: '#d4edda',
      ready: '#d4edda',
      normal: '#d4edda',
      warning: '#fff3cd',
      degraded: '#fff3cd',
      error: '#f8d7da',
      critical: '#f8d7da',
      offline: '#f8d7da',
      failed: '#f8d7da',
      unknown: '#e2e3e5'
    },

    default: {
      default: '#d4edda',
      ok: '#d4edda',
      check: '#f8d7da',
      active: '#fff3cd',
      noData: '#e2e3e5',
      info: '#d1ecf1',
    },

    utilization: {
      low: '#d4edda',
      medium: '#fff3cd',
      high: '#f8d7da',
      critical: '#f5c6cb',
    },

    performance: {
      excellent: '#d4edda',
      good: '#fff3cd',
      fair: '#ffeaa7',
      poor: '#fab1a0',
      critical: '#ff7675',
    },

    door: {
      closed: '#d4edda',    // Light green - closed
      open: '#fff3cd',      // Light yellow - open
      locked: '#d4edda',    // Light green - locked
      unlocked: '#fff3cd',  // Light yellow - unlocked
      error: '#f8d7da',     // Light red - error
      unknown: '#e2e3e5',    // Light gray - unknown
      notAvailable: '#f8f9fa' // Very light gray - not available
    },

    gripper: {
      balance: {
        balanced: '#d4edda', // Light green - balanced
        slightlyImbalanced: '#fff3cd', // Light yellow - slightly imbalanced
        imbalanced: '#ffeaa7', // Light orange - imbalanced
        highlyImbalanced: '#fab1a0', // Light red - highly imbalanced
      },

      imbalancePercentage: {
        low: '#d4edda', // Light green - low imbalance
        medium: '#fff3cd', // Light yellow - medium imbalance
        high: '#ffeaa7', // Light red - high imbalance
      }
    },

    cartridge: {
      state: {
        normal: '#d4edda',           // Light green
        unknown: '#f8f9fa',          // Light gray
        atEndOfLife: '#f8d7da',      // Light red
        failedVerification: '#f8d7da', // Light red
        exportQueued: '#fff3cd',     // Light yellow
        importing: '#d1ecf1',        // Light blue
        verifying: '#e2e3e5',        // Gray
        assignmentRequired: '#ffeaa7' // Light orange
      },

      accessibleStatus: {
        normal: '#d4edda',    // Light green
        limited: '#fff3cd',   // Light yellow
        no: '#f8d7da'         // Light red
      },
    },

    cleaningCartridge: {
      remaining: {
        depleted: '#f8d7da', // Light red - depleted
        critical: '#f5c6cb', // Dark red - critical
        low: '#fff3cd', // Light yellow - low
        good: '#d4edda' // Light green - good
      },

      usage: {
        low: '#d4edda',    // Light green
        medium: '#fff3cd', // Light yellow
        high: '#f8d7da'    // Light red
      },

      status: {
        good: '#d4edda',      // Light green
        low: '#fff3cd',       // Light yellow
        critical: '#f5c6cb',  // Dark red
        depleted: '#f8d7da'   // Light red
      }
    },

    dataCartridge: {
      lifetime: {
        critical: '#f8d7da', // Light red - critical
        warning: '#fff3cd', // Light yellow - warning
        good: '#d4edda' // Light green - good
      },
      utilization: {
        high: '#f8d7da', // Light red - high utilization
        medium: '#fff3cd', // Light yellow - medium utilization
        low: '#d4edda' // Light green - low utilization
      },
      encryption: {
        encrypted: '#d4edda',
        notEncrypted: '#fff3cd',
      }

    },

    diagnosticCartridge: {
      lifetime: {
        endOfLife: '#f8d7da', // Light red - critical
        lowRemaining: '#fff3cd', // Light yellow - warning
        good: '#d4edda' // Light green - good
      },
      usage: {
        high: '#f8d7da', // Light red - high usage
        medium: '#fff3cd', // Light yellow - medium usage
        low: '#d4edda' // Light green - low usage
      }
    },

    drive: {
      health: {
        healthy: '#27ae60',      // Green - healthy
        warning: '#f39c12',      // Orange - warning
        critical: '#e74c3c',     // Red - critical
        unknown: '#95a5a6'      // Gray - unknown
      }
    },

    frame: {
      state: {
        normal: '#d4edda',      // Light green
        warning: '#fff3cd',     // Light yellow
        error: '#f8d7da',       // Light red
        offline: '#f8f9fa',     // Light gray
        maintenance: '#e2e3e5'  // Gray
      },

      utilization: {
        low: '#d4edda',    // Light green
        medium: '#fff3cd', // Light yellow
        high: '#f8d7da',   // Light red
        critical: '#f5c6cb' // Dark red
      }
    }
  } as const;

  static reporter = {
    text: '#ffffff',
    title: {
      fill: '#233f4d',
      text: '#fff',
    },
    tableHeader: {
      text: '#000',
      fill: '#f0f5f8',
      hLine: () => '#a5a5a5',
      vLine: () => '#a5a5a5',
    },
    tableLayouts: {
      hLine: '#a5a5a5',
      vLine: '#a5a5a5',
    },
    thermometer: {
      critical: {
        bar: '#e74c3c',
        text: '#ffffff'
      },
      warning: {
        bar: '#ffbd4f',
        text: '#ffffff'
      },
      good: {
        bar: '#008000',
        text: '#ffffff'
      }
    }
  } as const;

  static taskServer = {
    heatmap: {
      read: {
        limit_2: '#008000',
        limit_2_4: '#29f929',
        limit_2_8: '#81fa51',
        limit_3_2: '#c9f433',
        limit_3_6: '#FFFF66',
        limit_4: '#ffff00',
        limit_4_6: '#FFCC00',
        limit_5: '#ffa500',
        limit_5_4: '#fa610e',
        limit_5_8: '#ff0000'
      },
      write: {
        limit_2: '#008000',
        limit_2_4: '#29f929',
        limit_2_8: '#81fa51',
        limit_3_2: '#c9f433',
        limit_3_6: '#FFFF66',
        limit_4: '#ffff00',
        limit_4_6: '#FFCC00',
        limit_5: '#ffa500',
        limit_5_4: '#fa610e',
        limit_5_8: '#ff0000'
      }
    }
  } as const;

  static advisor = {
    noted: Color.palette.green,
    warning: Color.palette.yellow,
    strongWarning: Color.palette.red,
    critical: Color.palette.redDark,
  } as const;

  static db2 = {
    metric: {
      capacity: {
        unused: Color.palette.red,
        used: Color.palette.green,
        available: Color.palette.blue,
        logSpace: Color.palette.orange,
      },
      agents: {
        stolen: Color.palette.red,
        idle: Color.palette.green,
        registered: Color.palette.blue,
        stop: Color.palette.orange,
        numPooled: Color.palette.yellow,
        numAssoc: Color.palette.brown,
        numCoord: Color.palette.purple,
      },
      locks: {
        held: Color.palette.red,
        waiting: Color.palette.orange,
        escalations: Color.palette.yellow,
        timeouts: Color.palette.blue,
        deadlocks: Color.palette.green,
      },
      io: {
        read: {
          logical: Color.palette.red,
          physical: Color.palette.green,
          direct: Color.palette.blue,
        },
        write: {
          writes: Color.palette.red,
          direct: Color.palette.blue,
        }
      },
      latency: {
        direct: {
          read: Color.palette.red,
          write: Color.palette.blue,
        },
        pool: {
          read: Color.palette.red,
          write: Color.palette.blue,
        },
        log: {
          wait: Color.palette.red,
        }
      },
      rows: {
        total: {
          modified: Color.palette.red,
          returned: Color.palette.green,
          read: Color.palette.blue,
          updated: Color.palette.orange,
          deleted: Color.palette.yellow,
          inserted: Color.palette.brown,
        },
        int: {
          deleted: Color.palette.red,
          inserted: Color.palette.green,
          updated: Color.palette.blue,
        },
        fed: {
          returned: Color.palette.red,
          read: Color.palette.green,
          updated: Color.palette.blue,
          deleted: Color.palette.orange,
          inserted: Color.palette.yellow,

        }
      },
      network: {
        remote: {
          send: Color.palette.red,
          receive: Color.palette.blue,
        },
        local: {
          send: Color.palette.red,
          receive: Color.palette.blue,
        },
        fcm: {
          send: Color.palette.red,
          receive: Color.palette.blue,
        }
      },
      query: {
        transaction: {
          commits: Color.palette.red,
          rollbacks: Color.palette.blue,
        },
        compilation: {
          lookups: Color.palette.red,
          inserts: Color.palette.blue
        }
      },
      waits: {
        total: {
          tcpipSendWaitTime: Color.palette.red,
          logDiskWaitTime: Color.palette.green,
          tcpipRecvWaitTime: Color.palette.blue,
          ipcSendWaitTime: Color.palette.orange,
          ipcRecvWaitTime: Color.palette.yellow,
          fcmSendWaitTime: Color.palette.brown,
          fcmRecvWaitTime: Color.palette.purple,
          cfWaitTime: Color.palette.black,
          lockWaitTime: Color.palette.red,
          agentWaitTime: Color.palette.green,
          wlmQueueTimeTotal: Color.palette.blue,
          poolReadTime: Color.palette.orange,
          poolWriteTime: Color.palette.yellow,
          directReadTime: Color.palette.brown,
          directWriteTime: Color.palette.purple,
        },
        io: {
          logDiskWaitTime: Color.palette.red,
          poolReadTime: Color.palette.green,
          poolWriteTime: Color.palette.blue,
          directReadTime: Color.palette.orange,
          directWriteTime: Color.palette.yellow,
        },
        network: {
          tcpipSendWaitTime: Color.palette.red,
          tcpipRecvWaitTime: Color.palette.blue,
          ipcSendWaitTime: Color.palette.orange,
          ipcRecvWaitTime: Color.palette.yellow,
          fcmSendWaitTime: Color.palette.red,
          fcmRecvWaitTime: Color.palette.blue,
        },
        misc: {
          cfWaitTime: Color.palette.black,
          lockWaitTime: Color.palette.red,
          agentWaitTime: Color.palette.green,
          wlmQueueTimeTotal: Color.palette.blue,
        }
      },
      session: {
        totalConnections: Color.palette.red,
        totalSecondaryConnections: Color.palette.green,
        applsCurConnections: Color.palette.blue,
        connectionsTop: Color.palette.orange,
      },
      ratio: {
        log: Color.palette.red,
        cache: Color.palette.green,
        buffer: Color.palette.blue,
        cPut: Color.palette.orange,
      },
      bufferPool: {
        ioRead: {
          logical: Color.palette.red,
          physical: Color.palette.green,
          direct: Color.palette.blue,
        },
        ioWrite: {
          writes: Color.palette.red,
          direct: Color.palette.blue,
        },
        latencyDirect: {
          read: Color.palette.red,
          write: Color.palette.blue,
        },
        latencyPool: {
          read: Color.palette.red,
          write: Color.palette.blue,
        },
        ratio: {
          log: Color.palette.red,
          cache: Color.palette.green,
          buffer: Color.palette.blue,
          cPut: Color.palette.orange,
        }
      }
    },
  } as const;

  static exagrid = {
    metric: {
      memory: {
        used: Color.palette.red,
        buffer: Color.palette.blue,
        cached: Color.palette.cyan,
        free: Color.palette.green,
      },
      backup: {
        landing: Color.palette.green,
        retention: Color.palette.blue,
      },
      consumption: {
        shares: Color.palette.red,
        replicas: Color.palette.orange,
        pending: Color.palette.purple,
      },
      deduplication: {
        dataRate: Color.palette.blue,
        capacityPending: Color.palette.red,
      }
    },
  } as const;

  static ibmCloud = {
    status: {
      ok: Color.palette.green,
      warning: Color.palette.yellow,
      critical: Color.palette.red,
    }
  } as const;

  static ibmTsm = {
    device: {
      volume: {
        count: Color.palette.black,
        error: Color.palette.red,
        noAccess: Color.palette.blue,
      },
      node: {
        count: Color.palette.black,
        locked: Color.palette.red,
      },
    },
    database: {
      capacity: {
        used: Color.palette.red,
        free: Color.palette.green,
      },
    },
    log: {
      used: Color.palette.red,
      free: Color.palette.green,
      active: {
        used: Color.palette.red,
        free: Color.palette.green,
      },
      failover: {
        used: Color.palette.red,
        free: Color.palette.green,
      },
      archive: {
        used: Color.palette.red,
        free: Color.palette.green,
      },
      mirror: {
        used: Color.palette.red,
        free: Color.palette.green,
      },
    },
    pool: {
      used: Color.palette.red,
      free: Color.palette.green,
      scratched: Color.palette.red,
    },
    activity: {
      used: Color.palette.red,
      examined: Color.palette.black,
    },
    volume: {
      used: Color.palette.red,
      free: Color.palette.green,
    },
    node: {
      used: Color.palette.red,
      free: Color.palette.green,
    },
    library: {
      private: Color.palette.blue,
      scratch: Color.palette.red,
    }

  } as const;

  static heatmap = {
    default: Color.palette.gray,
  } as const;

  static performance = {
    default: Color.palette.gray,
  } as const;

  static linux = {
    processors: Color.palette.black,
    cpu: {
      physical: Color.palette.redDark,
      core: Color.palette.redDark,
      load: Color.palette.redDark,
      loadAverage: Color.palette.brown,
      blocked: {
        processes: Color.palette.blueDark,
        raw: Color.palette.greenDark,
        direct: Color.palette.orangeDark,
      }
    },
    status: {
      ok: Color.palette.green,
      warning: Color.palette.yellow,
      critical: Color.palette.red,
    },
    metric: {
      cpu: {
        sys: Color.palette.blue,
        usr: Color.palette.yellow,
        wait: Color.palette.red,
        stolen: Color.palette.blue,
        idle: Color.palette.green,
        util: Color.palette.redDark,
        virtual: Color.palette.black,
        load: Color.palette.redDark,
        loadAverage: Color.palette.brown,
        blocked: Color.palette.blueDark,
        steal: Color.palette.brown,
        softirq: Color.palette.orange,
        nice: Color.palette.purple,
      },
      memory: {
        total: Color.palette.black,
        mem2: Color.palette.yellowDark,
        free: Color.palette.greenDark,
        mem4: Color.palette.black,
        used: Color.palette.redDark,
        cache: Color.palette.blueDark,
        util: Color.palette.redDark,
        wtsize: Color.palette.black,
        client: Color.palette.blueDark,
      },
      pages: {
        pgs1: Color.palette.blueDark,
        pgs2: Color.palette.redDark,
        pgs3: Color.palette.greenDark,
        pgs4: Color.palette.redDark,
      }
    },
  } as const;

  static mainframe = {
    cp: Color.palette.red,
    ifl: Color.palette.orange,
    icf: Color.palette.blue,
    iip: Color.palette.yellow,
    aap: Color.palette.green,
  } as const;

  static status = {
    ok: Color.palette.green,
    warning: Color.palette.yellow,
    critical: Color.palette.red,
  } as const;

  static vmware = {
    metric: {
      cpu: {
        allocated: Color.palette.green,
        usage: Color.palette.red,
        usageMax: Color.palette.blue,
        reservation: Color.palette.green,
        total: Color.palette.black,
        effective: Color.palette.blue,
      },
      memory: {
        total: Color.palette.black,
        granted: Color.palette.blue,
        consumed: Color.palette.cyan,
        active: Color.palette.red,
        baloon: Color.palette.yellow,
        swap: Color.palette.orange,
        reservation: Color.palette.purple,
      },
      swap: {
        out: Color.palette.red,
        in: Color.palette.blue,
      },
      compression: {
        decompress: Color.palette.red,
        compress: Color.palette.blue,
      },
      datastore: {
        capacity: Color.palette.green,
        used: Color.palette.red,
        provisioned: Color.palette.blueDark,
      }
    }
  } as const;

  static ovirt = {
    metric: {
      memory: {
        free: Color.palette.green,
        buffered: Color.palette.blue,
        cached: Color.palette.orange,
        used: Color.palette.red,
      },
      domain: {
        total: Color.palette.green,
        used: Color.palette.blue,
      },
    },
  } as const;

  static veeam = {
    job: {
      success: Color.palette.green,
      warning: Color.palette.orange,
      fail: Color.palette.red,
      active: Color.palette.blue,
      transferred: Color.palette.blue,
      processed: Color.palette.green,
      read: Color.palette.orange,
      processingRate: Color.palette.purple,
    },
  } as const;

  // FortiGate / FortiSwitch (LAN class) named graph colour slots.
  // Maps onto existing palette/metric values -- no new hex.
  static fortinet = {
    throughput: {
      in: Color.metric.network.throughput.read,
      out: Color.metric.network.throughput.write,
    },
    cpu: {
      wait: Color.palette.red,
      system: Color.palette.blue,
      user: Color.palette.yellow,
      idle: Color.palette.green,
      usage: Color.palette.redDark,
    },
    sla: {
      latency: Color.palette.redDark,
      jitter: Color.palette.orangeDark,
      packetLoss: Color.palette.purpleDark,
    },
    memory: {
      used: Color.metric.memory.used,
      free: Color.metric.memory.free,
      freeable: Color.palette.orange,
      usedPercent: Color.palette.redDark,
    },
    security: {
      virus: Color.palette.redDark,
      ips: Color.palette.yellowDark,
    },
    hardware: {
      temperature: Color.palette.orangeDark,
      fan: Color.palette.redDark,
      voltage: Color.palette.redDark,
    },
    radio: {
      retries: Color.palette.orangeDark,
      discards: Color.palette.purpleDark,
    },
    sessions: Color.palette.redDark,
    generic: Color.palette.redDark,
  } as const;

  static table = {
    background: {
      default: Color.palette.red,
    }
  } as const;

  static sqlServer = {
    metric: {
      memory: {
        free: Color.palette.red,
        total: Color.palette.green,
        target: Color.palette.blue,
        reserved: Color.palette.orange,
      },
      buffer: {
        background: Color.palette.red,
        checkpoint: Color.palette.green,
        lazy: Color.palette.blue,
      },
      readahead: {
        pages: Color.palette.red,
        times: Color.palette.green,
      },
      pageRW: {
        reads: Color.palette.red,
        writes: Color.palette.green,
      },
      page: {
        life: Color.palette.red,
        database: Color.palette.green,
        allocated: Color.palette.blue,
        free: Color.palette.orange,
        target: Color.palette.brown,
      },
      sessions: {
        user: Color.palette.red,
        reset: Color.palette.green,
        logins: Color.palette.blue,
        logouts: Color.palette.orange,
      },
      latch: {
        io: {
          dt: Color.palette.red,
          ex: Color.palette.green,
          kp: Color.palette.blue,
          nl: Color.palette.orange,
          sh: Color.palette.yellow,
          up: Color.palette.brown,
        },
        buffer: {
          dt: Color.palette.red,
          ex: Color.palette.green,
          kp: Color.palette.blue,
          nl: Color.palette.orange,
          sh: Color.palette.yellow,
          up: Color.palette.brown,
        },
        nonBuffer: {
          dt: Color.palette.red,
          ex: Color.palette.green,
          kp: Color.palette.blue,
          nl: Color.palette.orange,
          sh: Color.palette.yellow,
          up: Color.palette.brown,
        },
      },
      waitEvents: {
        events: {
          queryCompletion: Color.palette.red,
          schedulerYield: Color.palette.green,
          asyncNetworkIo: Color.palette.blue,
          msqlXp: Color.palette.orange,
          execsync: Color.palette.yellow,
          writeCompletion: Color.palette.brown,
          ioCompletion: Color.palette.purple,
          cxpacket: Color.palette.black,
          writelog: Color.palette.red,
          threadpool: Color.palette.green,
        },
        locks: {
          bu: Color.palette.red,
          is: Color.palette.green,
          iu: Color.palette.blue,
          ix: Color.palette.orange,
          rInNl: Color.palette.yellow,
          rInS: Color.palette.brown,
          rInU: Color.palette.purple,
          rInX: Color.palette.black,
          rsS: Color.palette.red,
          rsU: Color.palette.green,
          rxS: Color.palette.blue,
          rxU: Color.palette.orange,
          rxX: Color.palette.yellow,
          s: Color.palette.brown,
          schM: Color.palette.purple,
          schS: Color.palette.black,
          siu: Color.palette.red,
          six: Color.palette.green,
          u: Color.palette.blue,
          uix: Color.palette.orange,
          x: Color.palette.yellow,
        }
      },
      capacity: {
        unused: Color.palette.red,
        used: Color.palette.green,
        available: Color.palette.blue,
        logSpace: Color.palette.orange,
      },
      query: {
        transactions: {
          total: Color.palette.red,
          perSec: Color.palette.green,
          trackedPerSec: Color.palette.blue,
          writePerSec: Color.palette.orange,
          active: Color.palette.yellow,
        },
        cursors: {
          active: Color.palette.red,
          requestsPerSec: Color.palette.green,
          cachedCounts: Color.palette.blue,
        },
      },
      locks: {
        requestsPerSec: Color.palette.red,
        waitsPerSec: Color.palette.green,
        timeoutsPerSec: Color.palette.blue,
      }
    },
  } as const;

  static power = {
    server: {
      cpuPhysicalCores: Color.palette.redDark,
      cpuPhysicalCoresMax: Color.palette.redDark,
      cpuUtilizationCores: Color.palette.blueDark,
      cpuUtilizationCoresMax: Color.palette.blueDark,
      cpuUtilizationPercent: Color.palette.blueDark,
      cpuUtilizationPercentMax: Color.palette.blueDark,
      totalProcUnits: Color.palette.black,
      cpuPhysicalPercent: Color.palette.redDark,
      cpuPhysicalPercentMax: Color.palette.redDark,
      configurableProcUnits: Color.palette.black,
      assignedProcUnits: Color.palette.greenDark,
      availableProcUnits: Color.palette.greenDark,
      assignedProcUnitsPhysicalProcessor: Color.palette.black,
      utilizedProcUnitsPhysicalProcessor: Color.palette.redDark,
      configuredProcUnitsPhysicalProcessor: Color.palette.grayDark,
      borrowedProcUnitsPhysicalProcessor: Color.palette.yellowDark,
      availableProcUnitsPhysicalProcessor: Color.palette.greenDark,
      powerReading: Color.palette.redDark,
      assignedMinusBorrowed: Color.palette.greenDark,
      assignedMemToLpars: Color.palette.red,
      availableMem: Color.palette.green,
      totalMem: Color.palette.black,
      systemFirmwareUtilAssignedMem: Color.palette.blue,
    },
    cmcserver: {
      utilizedProcUnits: Color.palette.redDark,
      baseAnyOs: Color.palette.black,
      procInstalled: Color.palette.blueDark,
      coreAix: Color.palette.blue,
      baseCoreAix: Color.palette.black,
      coreIbmi: Color.palette.blue,
      baseCoreIbmi: Color.palette.black,
      coreOtherLinux: Color.palette.orange,
      coreVios: Color.palette.orange,
      baseCoreLinuxvios: Color.palette.black,
      coreRhel: Color.palette.red,
      baseCoreRhel: Color.palette.black,
      coreRhelcoreos: Color.palette.red,
      baseCoreRhcos: Color.palette.black,
      coreSles: Color.palette.green,
      baseCoreSles: Color.palette.black,
      memTotal: Color.palette.red,
      baseMemory: Color.palette.black,
      memInstalled: Color.palette.blueDark,
    },
    cmcpool: {
      baseCoreAnyOs: Color.palette.black,
      cmTotal: Color.palette.black,
      mmMinutes: Color.palette.redDark,
      mmTotal: Color.palette.redDark,
      memTotal: Color.palette.red,
      cmAix: Color.palette.blue,
      baseCoreAix: Color.palette.black,
      cmmAix: Color.palette.blueDark,
      mmmAix: Color.palette.blueDark,
      cmIbmi: Color.palette.blue,
      baseCoreIbmi: Color.palette.black,
      cmmIbmi: Color.palette.blueDark,
      mmmIbmi: Color.palette.blueDark,
      cmOtherlinux: Color.palette.orange,
      baseCoreLinuxvios: Color.palette.black,
      cmmLinux: Color.palette.orangeDark,
      mmmLinux: Color.palette.orangeDark,
      cmmLinuxvios: Color.palette.orangeDark,
      cmVios: Color.palette.blue,
      baseVios: Color.palette.black,
      cmmVios: Color.palette.blueDark,
      mmmVios: Color.palette.blueDark,
      cmRhel: Color.palette.red,
      baseCoreRhel: Color.palette.black,
      cmmRhel: Color.palette.redDark,
      mmmRhel: Color.palette.redDark,
      cmRhelcoreos: Color.palette.red,
      baseCoreRhcos: Color.palette.black,
      cmmRhelcoreos: Color.palette.redDark,
      mmmRhcos: Color.palette.redDark,
      cmSles: Color.palette.green,
      baseCoreSles: Color.palette.black,
      cmmSles: Color.palette.greenDark,
      mmmSles: Color.palette.greenDark,
    },
    vm: {
      cpuPhysicalCores: Color.palette.redDark,
      cpuPhysicalCoresMax: Color.palette.redDark,
      cpuUtilizationCores: Color.palette.blueDark,
      cpuUtilizationCoresMax: Color.palette.blueDark,
      cpuUtilizationPercent: Color.palette.blueDark,
      cpuUtilizationPercentMax: Color.palette.blueDark,
      entitledProcUnits: Color.palette.greenDark,
      maxVirtualProcessors: Color.palette.black,
      currentVirtualProcessors: Color.palette.black,
      entitledProcUnitsPercent: Color.palette.red,
      cpuPhysicalPercent: Color.palette.redDark,
      cpuPhysicalPercentMax: Color.palette.redDark,
      backedPhysicalMem: Color.palette.blueDark,
      logicalMem: Color.palette.redDark,
      utilizedMem: Color.palette.brownDark,
      assignedMem: Color.palette.purpleDark,
      virtualPersistentMem: Color.palette.yellowDark,
    },
    pool: {
      utilizedProcUnits: Color.palette.redDark,
      utilizedProcUnitsMax: Color.palette.redDark,
      reservedProcessingUnits: Color.palette.greenDark,
      configuredProcUnits: Color.palette.orangeDark,
      assignedProcUnits: Color.palette.blueDark,
      maximumProcessingUnits: Color.palette.black,
      availableProcUnits: Color.palette.greenDark,
      borrowedProcUnits: Color.palette.greenDark,
      totalUtilizedProcUnitsPercent: Color.palette.redDark,
      totalUtilizedProcUnitsPercentMax: Color.palette.redDark,
    },
  } as const;

  static timescaleDb = {
    metric: {
      sessions: {
        numbackends: Color.palette.black,
        active: Color.palette.green,
        idle: Color.palette.blue,
        idleintransaction: Color.palette.brown,
        idleintransactionaborted: Color.palette.red,
        fastpathfunctioncall: Color.palette.yellow,
        disabled: Color.palette.orange,
      },
      txn: {
        xactrollback: Color.palette.blue,
        xactcommit: Color.palette.red,
      },
      locks: {
        deadlocks_max: Color.palette.red,
        relation: Color.palette.green,
        extend: Color.palette.blue,
        page: Color.palette.brown,
        tuple: Color.palette.purple,
        object: Color.palette.yellow,
        userlock: Color.palette.orange,
      }
    },
  } as const;

  static morpheus = {
    metrics: {
      cpu: {
        used: Color.palette.redDark,
        total: Color.palette.blueDark,
      },
      memory: {
        used: Color.palette.redDark,
        total: Color.palette.blueDark,
        vme: {
          used: Color.palette.red,
          free: Color.palette.green,
          total: Color.palette.blackDark,
        },
        system: {
          used: Color.palette.red,
          free: Color.palette.green,
          total: Color.palette.blackDark,
        },
      },
      storage: {
        used: Color.palette.red,
        free: Color.palette.green,
        total: Color.palette.blackDark,
      },
      cluster: {
        cpu: {
          used: Color.palette.redDark,
          peak: Color.palette.blueDark,
          total: Color.palette.blackDark,
        },
        memory: {
          used: Color.palette.red,
          reserved: Color.palette.orangeDark,
          hostReserved: Color.palette.purpleDark,
          total: Color.palette.blackDark,
        },
        storage: {
          used: Color.palette.red,
          total: Color.palette.blackDark,
        },
      },
      host: {
        cpu: {
          user: Color.palette.red,
          system: Color.palette.blue,
          idle: Color.palette.green,
        },
        memory: {
          used: Color.palette.red,
          free: Color.palette.green,
          reservedFree: Color.palette.orange,
          reserved: Color.palette.blue,
          uncommittedFree: Color.palette.green,
          total: Color.palette.blackDark,
          usedPercent: Color.palette.redDark,
          committedPercent: Color.palette.orangeDark,
          overcommitReserved: Color.palette.orangeDark,
          overcommitHostReserved: Color.palette.blueDark,
        },
        storage: {
          used: Color.palette.red,
          free: Color.palette.green,
          reservedFree: Color.palette.orange,
          total: Color.palette.blackDark,
          usedPercent: Color.palette.redDark,
          committedPercent: Color.palette.orangeDark,
          overcommitReserved: Color.palette.orangeDark,
        },
        swap: {
          used: Color.palette.red,
          free: Color.palette.green,
        },
      },
      instance: {
        cpu: {
          user: Color.palette.red,
          system: Color.palette.blue,
        },
        storage: {
          used: Color.palette.red,
          total: Color.palette.blackDark,
        },
      },
      vm: {
        cpu: {
          user: Color.palette.red,
          system: Color.palette.blue,
        },
        memory: {
          used: Color.palette.red,
          free: Color.palette.green,
          total: Color.palette.blackDark,
        },
        storage: {
          used: Color.palette.red,
          total: Color.palette.blackDark,
        },
      },
      volume: {
        used: Color.palette.red,
        total: Color.palette.blackDark,
      },
      datastore: {
        used: Color.palette.red,
        free: Color.palette.green,
        total: Color.palette.blackDark,
      },
    },

  } as const;
};